import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  ArrowRight,
  Heart,
  Activity,
  Sparkles,
  Home,
  Building,
  MapPin,
  Calendar,
  RefreshCw,
  Users,
  FileText,
  Briefcase,
  Star,
  Compass
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  heart: Heart,
  activity: Activity,
  sparkles: Sparkles,
  home: Home,
  building: Building,
  mappin: MapPin,
  calendar: Calendar,
  refresh: RefreshCw,
  users: Users,
  filetext: FileText,
  briefcase: Briefcase,
  star: Star,
  compass: Compass
};

const ConsultationFeesPage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/consultation-services`);
      if (response.ok) {
        const data = await response.json();
        setServices(data.services || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group services by category
  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {});

  const categoryOrder = ['Consultations', 'Astrology Readings', 'Spiritual Guidance'];
  const categoryDescriptions = {
    'Consultations': 'For additional information about each service, click on the cards below.',
    'Astrology Readings': 'For additional information about each reading, click on the cards below.',
    'Spiritual Guidance': 'For additional information about each service, click on the cards below.'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-amber-400 text-sm uppercase tracking-widest mb-4">Life Decoder</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6">
              Consultation <span className="font-serif italic text-amber-200">Fees</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Transparent pricing for all our Chinese Metaphysics consultation services
            </p>
          </div>
        </div>
      </section>

      {/* Services by Category */}
      {categoryOrder.map((category) => {
        const categoryServices = groupedServices[category];
        if (!categoryServices || categoryServices.length === 0) return null;

        return (
          <section key={category} className="py-16 border-t border-gray-800/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Category Header */}
              <div className="text-center mb-12">
                <p className="text-amber-400 text-sm uppercase tracking-widest mb-3">Life Decoder</p>
                <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
                  {category.split(' ')[0]} <span className="font-serif italic text-amber-200">{category.split(' ').slice(1).join(' ')}</span>
                </h2>
                <p className="text-gray-500">{categoryDescriptions[category]}</p>
              </div>

              {/* Services Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {categoryServices.map((service) => {
                  const IconComponent = iconMap[service.icon] || Sparkles;
                  return (
                    <div
                      key={service.id}
                      onClick={() => navigate(`/consultation-fees/${service.slug}`)}
                      className="group cursor-pointer bg-gray-900/30 border border-gray-800 rounded-xl overflow-hidden hover:border-amber-500/50 transition-all duration-300"
                    >
                      {/* Image */}
                      <div className="aspect-[4/3] relative overflow-hidden">
                        {service.image ? (
                          <img
                            src={service.image}
                            alt={service.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-amber-900/30 to-gray-900 flex items-center justify-center">
                            <IconComponent className="w-16 h-16 text-amber-500/50" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <h3 className="text-lg font-medium text-white mb-2 group-hover:text-amber-200 transition-colors">
                          {service.name}
                        </h3>
                        <p className="text-amber-400 font-medium mb-3">
                          Starting at ${service.starting_price}
                        </p>
                        <div className="flex items-center text-gray-400 text-sm group-hover:text-amber-400 transition-colors">
                          <span>Read More</span>
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-t from-amber-900/10 to-transparent">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Not sure which service is right for you?
          </h2>
          <p className="text-gray-400 mb-8">
            Book a discovery call and we'll help you find the perfect consultation for your needs.
          </p>
          <Button
            onClick={() => navigate('/consultation-fees')}
            className="bg-amber-500 hover:bg-amber-400 text-black font-medium px-8 py-3"
          >
            Book a Discovery Call
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ConsultationFeesPage;
